import {z} from 'zod'
import {uri} from 'shared/uri'
import {apiClient} from 'shared/apiClient'
import {zRoles} from 'shared/forecast/roleTypes'

export namespace Auth {
  const signUpCommon = {
    email: z.string(),
    password: z.string()
  }
  const zSignUpReq = z.discriminatedUnion('mode', [
    z.object({
      mode: z.literal('open-invite'),
      workspace: z.string(),
      invite: z.string(),
      ...signUpCommon
    }),
    z.object({
      mode: z.literal('email-invite'),
      workspace: z.string(),
      invite: z.string(),
      ...signUpCommon
    }),
    z.object({
      mode: z.literal('sign-up'),
      ...signUpCommon
    }),
    z.object({
      mode: z.literal('anon-signup'),
      ...signUpCommon
    })
  ])

  const otpTypes = ['invite', 'recovery', 'magiclink'] as const

  export const client = apiClient({
    md: {
      verifyEmail: {zReq: z.object({email: z.string(), otpType: z.enum(otpTypes)})},
      signUp: {
        zReq: zSignUpReq,
        zResOk: z.object({
          auto_confirmed: z.boolean().optional(),
          message: z.string().optional(),
          old_user_id: z.string().optional(),
          replacement_token: z.string().optional()
        }),
        zResErr: z.object({message: z.string(), errorType: z.enum(['invite', 'user-exists']).optional()})
      },
      sendInvites: {zReq: z.object({ids: z.array(z.string())})},
      anonReplacement: {zReq: z.object({old_user_id: z.string(), replacement_token: z.string()})},
      shareLink: {
        zReq: z.object({
          emails: z.array(z.string()),
          role: zRoles,
          workspace_id: z.string(),
          path: z.string(),
          title: z.string()
        }),
        zResOk: z.object({
          emailsSent: z.number()
        })
      }
    },
    url: uri('/api/auth')
  } as const)

  export const signUp = client.makePost('signUp')
  export type SignUpRequest = Parameters<typeof signUp>[0]
  export type SignUpResult = Awaited<ReturnType<typeof signUp>>

  export const verifyEmail = client.makePost('verifyEmail')
  export type VerifyEmailRequest = Parameters<typeof verifyEmail>[0]

  export const sendInvites = client.makePost('sendInvites')
  export type SendInvitesRequest = Parameters<typeof sendInvites>[0]

  /** Replaces the anon user with the current user.
   *
   * This is used when a user wants to upgrade their anonymous account to a real account.
   *
   * The anon user is deleted and the new user is created with the same email.
   * If a profile already exists for the email, the anon users details are migrated to the existing profile.
   */
  export const anonReplacement = client.makePost('anonReplacement')
  export type AnonReplacementRequest = Parameters<typeof anonReplacement>[0]

  export const shareLink = client.makePost('shareLink')
  export type ShareLinkRequest = Parameters<typeof shareLink>[0]
  export type ShareLinkResult = Awaited<ReturnType<typeof shareLink>>
}
